import type { FormInstance, UploadProps } from 'antd';
import { Button, Flex, Typography, Upload } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { Camera, CloudUpload, FileEarmarkImage, Trash } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { FileTypeEnum } from '../../enums/FileTypeEnum';
import { getBase64 } from '../../helpers/utilityHelper';
import { normalizeFileMeta } from '../../utils/normalizeFileMeta';
const { Text } = Typography;
const { Dragger } = Upload;

interface UploadComProps {
  name: string | (string | number)[];
  form: FormInstance;
  maxCount: number;
  maxSize?: number;
  fileTypes?: FileTypeEnum[];
  initialFiles?: any;
  isDisabled?: boolean;
  cameraOnly?: boolean;
}

const FormDragDropUpload: React.FC<UploadComProps> = ({
  name,
  form,
  maxCount = 1,
  fileTypes,
  maxSize = 10,
  initialFiles = [],
  isDisabled,
  cameraOnly,
}) => {
  const { t } = useTranslation('form');
  const [uploadFileList, setUploadFileList] = useState<any>([]);
  const [showError, setShowError] = useState<string | undefined>(undefined);
  const [isDisabledStatus, setIsDisabledStatus] = useState(isDisabled);
  useEffect(() => {
    const currentFieldValue = form.getFieldValue(name);
    if (JSON.stringify(currentFieldValue) !== JSON.stringify(uploadFileList)) {
      form.setFieldValue(name, uploadFileList);
    }
  }, [uploadFileList, name, form]);

  const FILE_TYPE_ERROR = `${t('invalidFileType')} ${fileTypes?.map((file) => file.toLocaleUpperCase()).join(', ')}.`;
  const FILE_SIZE_ERROR = `${t('fileTooLarge', { size: maxSize })} `;
  const FILE_MAX_COUNT_ERROR = `${t('maxFileCount')} `;
  /* useEffect(() => {
    if (initialFiles.length > 0 && initialFiles[0]) {
      setIsFileSet(true);
      const base64 = initialFiles;
      const fileName = 'cne_file.xlsx';
      const mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const dataUrl = `data:${mimeType};base64,${base64}`;
      setUploadFileList([
        {
          image: base64,
          dataUrl,
          id: 'uid',
          name: fileName,
          size: 'n/a',
          type: mimeType,
        },
      ]);
    }
  }, [initialFiles]); */

  useEffect(() => {
    const fieldFiles = form.getFieldValue(name);
    if (fieldFiles?.length > 0) {
      const formatted = fieldFiles.map((file: any, index: number) =>
        normalizeFileMeta(file, index)
      );
      setUploadFileList(formatted);
    } else if (initialFiles?.length > 0) {
      const formatted = initialFiles.map((file: any, index: number | undefined) =>
        normalizeFileMeta(file, index)
      );
      setUploadFileList(formatted);
      const currentFieldValue = form.getFieldValue(name);
      if (!currentFieldValue || currentFieldValue.length === 0) {
        form.setFieldValue(name, formatted);
      }
    }
  }, [initialFiles, name, form]);

  useEffect(() => {
    if (isDisabled !== undefined) {
      setIsDisabledStatus(isDisabled);
    } else if (uploadFileList.length >= maxCount) {
      setIsDisabledStatus(true);
    } else {
      setIsDisabledStatus(false);
    }
  }, [isDisabled, uploadFileList, maxCount]);

  const onRemoveImage = (file: any) => {
    // setIsFileSet(false);
    setIsDisabledStatus(false);
    setUploadFileList((prev: any) => {
      const list = prev.filter((exFile: any) => exFile?.id !== file.id);
      return list;
    });
  };

  const upProps: UploadProps = {
    maxCount: maxCount,
    name: Array.isArray(name) ? name.join('.') : name,
    multiple: true,
    showUploadList: false,
    ...(cameraOnly ? { accept: 'image/*', capture: 'environment' } : {}),
    beforeUpload: async (file) => {
      if (uploadFileList.length === maxCount) {
        setShowError(FILE_MAX_COUNT_ERROR);
        setTimeout(() => {
          setShowError(undefined);
        }, 4000);
        return;
      }

      const extension = file.name.split('.').pop()?.toLowerCase();

      if (!fileTypes?.includes(extension as FileTypeEnum)) {
        setShowError(FILE_TYPE_ERROR);
        setTimeout(() => {
          setShowError(undefined);
        }, 4000);
        return false;
      }

      const isAllowedSize = file.size / 1024 / 1024 < maxSize;
      if (!isAllowedSize) {
        setShowError(FILE_SIZE_ERROR);
        setTimeout(() => {
          setShowError(undefined);
        }, 4000);
        return false;
      }

      const image = await getBase64(file);
      const [_, actualImage] = image.split(',');
      setUploadFileList((prev: any) => {
        // setIsFileSet(true);
        const list = [
          ...prev,
          {
            image: actualImage,
            dataUrl: image,
            id: file.uid,
            name: file.name,
            size: file.size,
            type: file.type,
          },
        ];
        return list;
      });
      return false;
    },
    onRemove(file) {
      setUploadFileList((prev: any) => {
        const list = prev.filter((exFile: any) => exFile?.id !== file.uid);
        return list;
      });
    },
    onChange: async (info) => {
      const file: any = info.file;
      if (!file) return;

      if (!fileTypes || fileTypes.length === 0) {
        return true;
      }
      const extension = file.name.split('.').pop()?.toLowerCase();
      const isAllowedSize = file.size / 1024 / 1024 < maxSize;
      const isAllowType = fileTypes.includes(extension);
      if (!isAllowType || isAllowedSize) return;
      if (showError) {
        // setShowError(undefined);
        return false;
      }

      const image = await getBase64(info.file);
      const [_, actualImage] = image.split(',');
      setUploadFileList((prev: any) => {
        const list = [
          ...prev,
          {
            image: actualImage,
            dataUrl: image,
            id: file.uid,
            name: file.name,
            size: file.size,
            type: file.type,
          },
        ];
        return list;
      });
    },
  };

  const fileTypeAsString = useCallback(() => {
    return fileTypes
      ?.map((type) => type.toUpperCase())
      .join(', ')
      .replace(/, ([^,]*)$/, ' or $1');
  }, [fileTypes]);

  const truncateFileName = (name: string, maxLength: number = 14) => {
    if (!name) return '';
    return name.length > maxLength ? name.substring(0, maxLength) + '...' : name;
  };

  return (
    <>
      <div className="my-2">
        {uploadFileList.map((file: any) => {
          return (
            <Flex
              key={file.id}
              align="center"
              className="group"
              style={{
                border: '1px solid #CCCCCC',
                borderRadius: '100px',
                width: 'fit-content',
                paddingLeft: '10px',
                paddingRight: '10px',
                paddingTop: '5px',
                paddingBottom: '5px',
              }}
            >
              <FileEarmarkImage className="mr-1 text-primary"></FileEarmarkImage>{' '}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  const link = document.createElement('a');
                  const isURL = file.image?.startsWith('http');
                  link.href = isURL
                    ? file.image
                    : file.dataUrl || `data:${file.type};base64,${file.image}`;
                  link.download = file.name || 'download.xlsx';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                className="ml-2 hover:text-blue-600"
              >
                {truncateFileName(file.name)}
              </a>
              {!isDisabledStatus && (
                <Trash
                  className="ml-4 cursor-pointer text-primary-red"
                  onClick={() => {
                    onRemoveImage(file);
                  }}
                ></Trash>
              )}
            </Flex>
          );
        })}
      </div>
      <Dragger {...upProps} disabled={isDisabledStatus} className="upload-dragger w-full">
        <Flex vertical className="w-full items-center justify-center" gap={10}>
          <p className="flex justify-center">
            {cameraOnly ? <Camera className="text-3xl" /> : <CloudUpload className="text-3xl" />}
          </p>
          <p className="ant-upload-text text-center !text-xs leading-tight md:!text-sm">
            {t(cameraOnly ? 'takePhotoMessage' : 'selectFileMessage')}
          </p>
          <p className="ant-upload-hint text-center !text-[11px] leading-tight md:!text-sm">
            {!cameraOnly &&
              t('fileTypes', {
                fileType: fileTypeAsString(),
                fileSize: maxSize,
              })}
          </p>
          <p className="ant-upload-hint !text-[10px] leading-tight text-gray-500 md:!text-xs">
            {t(cameraOnly ? 'maxPhotosAllowed' : 'maxFilesAllowed', { count: maxCount })}
          </p>
          <Button
            className="btn-primary-border !text-xs uppercase md:!text-sm"
            disabled={isDisabledStatus}
          >
            {t(cameraOnly ? 'takePhoto' : 'selectFile')}
          </Button>
        </Flex>
      </Dragger>
      {showError && <p className="ant-form-item-explain-error">{showError}</p>}
    </>
  );
};

export default FormDragDropUpload;
